import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useStartWorkflow, type PlainWorkflow } from "@/queries/automation-queries";
import { useListProjects } from "@/queries/project-queries";
import { formatError } from "@/utils/errors";
import { workflowExecutionsUrl, workflowName } from "@/utils/automations";
import { WorkflowEditCodeContext } from "@/components/automations/AutomationEditCodeContext";
import {
    convertFormContextToProtobuf,
    getInitialContext,
    WorkflowEditFormTriggerContextType,
    type WorkflowEditFormTriggerContext,
} from "@/components/automations/automation-edit-form-data";

type RunAutomationDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    workflow: PlainWorkflow;
};

export const RunAutomationDialog: FC<RunAutomationDialogProps> = ({ open, onOpenChange, workflow }) => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const startWorkflow = useStartWorkflow();

    const [context, setContext] = useState<WorkflowEditFormTriggerContext>(() => getInitialContext(workflow));
    useEffect(() => {
        if (open) {
            // Reset context when dialog opens or workflow changes
            setContext(getInitialContext(workflow));
        }
    }, [open, workflow]);

    const projectIdsToFetch = useMemo(
        () => (context.type === WorkflowEditFormTriggerContextType.Projects ? context.projectIds : []),
        [context],
    );
    const { data: accessibleProjectsData } = useListProjects({
        enabled: projectIdsToFetch.length > 0,
        projectIds: projectIdsToFetch,
    });

    const handleRun = useCallback(async () => {
        try {
            let filteredContext = context;

            // Filter out projects the user doesn't have access to
            if (context.type === WorkflowEditFormTriggerContextType.Projects && accessibleProjectsData?.projects) {
                const accessibleProjectIds = new Set(accessibleProjectsData.projects.map((p) => p.id));
                filteredContext = {
                    ...context,
                    projectIds: context.projectIds.filter((id) => accessibleProjectIds.has(id)),
                };
            }

            const contextOverride = convertFormContextToProtobuf(filteredContext);
            const execution = await startWorkflow.mutateAsync({
                workflowId: workflow.id,
                contextOverride,
            });
            onOpenChange(false);
            navigate(workflowExecutionsUrl({ id: execution.id }));
        } catch (error) {
            toast({
                title: "Failed to start automation",
                description: formatError(error),
            });
        }
    }, [context, accessibleProjectsData, workflow.id, startWorkflow, onOpenChange, navigate, toast]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!startWorkflow.isPending) {
                onOpenChange(open);
            }
        },
        [onOpenChange, startWorkflow.isPending],
    );

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                data-testid="run-automation-dialog"
                data-track-location={TrackLocations.RunAutomationModal}
                // Allowing overflow to the combobox can overflow the dialog
                className="max-w-2xl overflow-visible"
            >
                <DialogHeader>
                    <DialogTitle>Manually run &apos;{workflowName(workflow)}&apos;</DialogTitle>
                </DialogHeader>

                <DialogBody>
                    <WorkflowEditCodeContext
                        context={context}
                        onChange={setContext}
                        disabled={startWorkflow.isPending}
                    />
                </DialogBody>

                <DialogFooter className="justify-end">
                    <DialogClose asChild>
                        <Button
                            variant="outline"
                            disabled={startWorkflow.isPending}
                            data-testid="run-automation-dialog-cancel"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="primary"
                        onClick={handleRun}
                        disabled={startWorkflow.isPending}
                        loading={startWorkflow.isPending}
                        data-testid="run-automation-dialog-run"
                        data-tracking-id="run-automation-dialog-run"
                        autoFocus={true}
                    >
                        Run
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
